package ai.beakman.undercover.data

import ai.beakman.undercover.model.Player
import ai.beakman.undercover.model.Room
import ai.beakman.undercover.model.Vote
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

/**
 * Repositório do modo Online (Firestore via GitLive). Funciona em android/ios/js.
 * Modelo público (sem auth): identidade por clientId; doc id de player/vote == clientId.
 *
 * Firebase deve estar inicializado pela plataforma antes do primeiro uso:
 *  - Android: automático (plugin google-services + google-services.json)
 *  - iOS: FirebaseApp.configure() no app Swift (via GoogleService-Info.plist)
 *  - Web/JS: Firebase.initialize(...) no entry do webApp com o firebaseConfig
 */
class RoomRepository {
    private val db by lazy { Firebase.firestore }

    private fun roomDoc(code: String) = db.collection("rooms").document(code)
    private fun playersCol(code: String) = roomDoc(code).collection("players")
    private fun votesCol(code: String) = roomDoc(code).collection("votes")

    // --- Sala ---
    suspend fun createRoom(room: Room) {
        roomDoc(room.code).set(room)
    }

    suspend fun roomExists(code: String): Boolean =
        roomDoc(code).get().exists

    /** Sobrescreve o doc da sala (host dirige as transições de stage). */
    suspend fun saveRoom(room: Room) {
        roomDoc(room.code).set(room)
    }

    fun observeRoom(code: String): Flow<Room?> =
        roomDoc(code).snapshots.map { snap -> if (snap.exists) snap.data<Room>() else null }

    // --- Jogadores ---
    suspend fun joinPlayer(code: String, player: Player) {
        playersCol(code).document(player.clientId).set(player)
    }

    suspend fun setReady(code: String, clientId: String, ready: Boolean) {
        playersCol(code).document(clientId).update("ready" to ready)
    }

    suspend fun resetAllReady(code: String) {
        playersCol(code).get().documents.forEach { it.reference.update("ready" to false) }
    }

    fun observePlayers(code: String): Flow<List<Player>> =
        playersCol(code).snapshots.map { qs ->
            qs.documents.map { it.data<Player>() }.sortedBy { it.joinOrder }
        }

    /** Saída explícita: remove o jogador (e o voto dele) da sala. */
    suspend fun leaveRoom(code: String, clientId: String) {
        votesCol(code).document(clientId).delete()
        playersCol(code).document(clientId).delete()
    }

    /** Encerra a sala (ex.: host saiu no lobby): apaga votos, jogadores e o doc da sala. */
    suspend fun deleteRoom(code: String) {
        votesCol(code).get().documents.forEach { it.reference.delete() }
        playersCol(code).get().documents.forEach { it.reference.delete() }
        roomDoc(code).delete()
    }

    // --- Votos ---
    suspend fun castVote(code: String, vote: Vote) {
        votesCol(code).document(vote.voterId).set(vote)
    }

    suspend fun clearVotes(code: String) {
        votesCol(code).get().documents.forEach { it.reference.delete() }
    }

    fun observeVotes(code: String): Flow<List<Vote>> =
        votesCol(code).snapshots.map { qs -> qs.documents.map { it.data<Vote>() } }
}
